import { Component, inject, Output, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subject, take, takeUntil, tap } from 'rxjs';

import { Role } from '@core/enums/roles';
import { IDistrict } from '@core/interfaces/ilookup';
import { IUser } from '@core/interfaces/iuser';
import { LookupService } from '@core/services/lookup.service';
import { UserManagementService } from '@views/features/management/services/user-management.service';
import { AuthService } from '@core/services/auth.service';
import { ReportService } from '../services/report.service';
import { IReportResponse, ISearchReportResponse } from '../interfaces/ireport-response';
import { ReportHeaders } from '../enums/report-headers';
import { IReportRequest, IReportCardRequest } from '../interfaces/ireport-request';
import { DateFormatPipe } from '@core/pipes/date-format.pipe';
import { ISelect } from '@shared/components/dropdowns/interfaces/iselect';
import { SchoolYear } from '../enums/school-year';
import { ISchoolYear } from '@core/interfaces/ischool-year';
import { IColumnSort } from '@core/interfaces/itable';

@Component({
  selector: 'prism-report-course-view',
  templateUrl: './report-course-view.component.html',
  styleUrl: './report-course-view.component.scss',
})
export class ReportCourseViewComponent implements OnChanges {
  @Output() reportSearched = new EventEmitter<void>();
  @Input() schoolYearId: number = SchoolYear.SY2026;;
  private fb = inject(FormBuilder);
  private lookupService = inject(LookupService);
  private authService = inject(AuthService);
  private reportService = inject(ReportService);
  private dateFormatter = inject(DateFormatPipe);
  userManagementService = inject(UserManagementService);

  role = Role;
  colHeaders = ReportHeaders;

  searchForm!: FormGroup;
  organizationOptions: IDistrict[] = [];
  selectedCourse!: ISelect;

  user: IUser = this.authService.getCurrentUser();
  pageTitle: string = 'Filters';
  pageIndex: number = 1;
  pageSize: number = 10;
  totalData: number = 0;
  data: IReportResponse[] = [];
  columns: string[] = ['fullName', 'courseName', 'programYear', 'status', 'districtName', 'schoolName', 'completedOn'];

  completionStatusOptions = [
    { id: 'Completed', name: 'Completed' },
    { id: 'Incomplete', name: 'Incomplete' },
    { id: 'Not Started', name: 'Not Started' },
  ];
  currentDistrict: IDistrict | null = null;

  get search() {
    return this.searchForm.get('search');
  }
  get organization() {
    return this.searchForm.get('organization');
  }
  get course() {
    return this.searchForm.get('course');
  }
  get schoolYear() {
    return this.searchForm.get('schoolYear');
  }
  get isCompleted() {
    return this.searchForm.get('isCompleted');
  }
  get completionStatus(): FormControl {
    return this.searchForm.get('completionStatus') as FormControl;
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadOrganizations();
    this.listenToFormChanges();
    this.getCurrentSchoolYear();
    this.schoolYearListener();
    this.searchReport();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schoolYearId'] && !changes['schoolYearId'].firstChange) {
      this.searchReport();
    }
  }

  initializeForm(): void {
    this.searchForm = this.fb.group({
      search: [''],
      organization: [null],
      course: [null],
      schoolYear: [null],
      isCompleted: [null],
      completionStatus: [null],
      pageIndex: this.pageIndex,
      ItemsPerPage: this.pageSize,
      sortDirection: ['asc'],
      sortColumn: [null]
    });

    this.selectedCourse = {
      control: this.course,
      label: 'Course',
      required: false,
      readOnly: false,
      labelClass: '',
    };
  }

  loadOrganizations(): void {
  this.lookupService.getOrganizations().pipe(
    take(1),
    tap((districts: IDistrict[]) => {
      this.organizationOptions = districts;
    })
  ).subscribe();
}

  listenToFormChanges(): void {
    this.organization?.valueChanges.pipe(
      tap(value => {
        const org = this.organizationOptions.find(o => o.id === value);
        this.currentDistrict = org || null;
        this.search?.setValue(org?.name || '');

        this.authService.setCurrentDistrict(this.currentDistrict);
        this.lookupService.districtSubject$.next(this.currentDistrict);

        this.searchReport();
      })
    ).subscribe();

    this.course?.valueChanges.pipe(
      tap(() => this.searchReport())
    ).subscribe();

    this.completionStatus.valueChanges.pipe(
      tap(() => this.onCompletionStatusChange())
    ).subscribe();
  }

  buildReportPayload(): IReportRequest {
    return {
      ...this.searchForm.value,
      courseId: this.course?.value,
      schoolYearId: this.schoolYear?.value,
      completionStatus: this.completionStatus?.value || null,
      pageIndex: this.pageIndex,
      ItemsPerPage: this.pageSize,
      sortDirection: this.searchForm.get('sortDirection')?.value,
      sortColumn: this.searchForm.get('sortColumn')?.value
    };
  }

  searchReport(): void {
    const reportPayload = this.buildReportPayload();
    this.reportService.searchReport(reportPayload).pipe(
      take(1),
      tap((res: ISearchReportResponse) => {
        this.data = res.items.map(item => ({
          ...item,
          completedOn: item.completedOn ? this.dateFormatter.transform(item.completedOn) : '',
        }));
        this.pageIndex = res.pageIndex;
        this.totalData = res.totalData;
        this.lookupService.districtSubject$.next(this.currentDistrict);
      })
    ).subscribe();
  }

  getCurrentSchoolYear(): void {
    this.lookupService.getCurrentSchoolYear()
      .pipe(
        take(1),
        tap((res: ISchoolYear) => {
          this.lookupService.schoolYearSubject$.next(res.id);
        })
      )
      .subscribe();
  }

  downloadReport(): void {
    this.reportService.downloadReport(this.buildReportPayload()).pipe(
      take(1)
    ).subscribe((res: any) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.body);
      a.setAttribute('download', 'prism_course_report.xlsx');
      a.click();
    });
  }

  handlePageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.searchReport();
  }

  handlePageSizeChange(pageSize: number): void {
    if (pageSize == null || pageSize === this.pageSize) return;
    this.pageSize = pageSize;
    this.searchReport();
  }

  onOrganizationSelected(districtId: Event | number | null): void {
    const selectedDistrict = this.organizationOptions.find(o => o.id === districtId) || null;
    this.lookupService.districtSubject$.next(selectedDistrict);
    this.authService.setCurrentDistrict(selectedDistrict || null);
  }

   handleColumnDirectionChange(columnSort: IColumnSort): void {
    this.searchForm.patchValue({
      sortDirection: columnSort.direction,
      sortColumn: columnSort.column
    });
    this.searchReport();
  }

  onCompletionStatusChange(): void {
    this.pageIndex = 1;
    this.searchReport();
  }

  schoolYearListener(): void {
    this.schoolYear?.valueChanges
      .pipe(tap((val: number | null) => {
        this.lookupService.schoolYearSubject$.next(val);
        this.searchReport();
      }))
      .subscribe();
  }
}